import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { teamAPI } from '../lib/api';
import { isAuthenticated } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, LogIn, UserPlus, Users } from 'lucide-react';

/**
 * `/team/invite?token=<jwt>`. Landing for the email's "Accept invitation"
 * button.
 *
 * Three branches based on auth state and invite state:
 *
 *   1. Not logged in: stash the token in sessionStorage and bounce to
 *      `/auth?redirect=/team/invite`. After login or register, the user
 *      lands back here with the token still in URL → branch 2.
 *   2. Logged in + token: call `/api/team/invite/accept`. On success →
 *      navigate to /team. On 409 (already a member, or invite consumed)
 *      → show conflict UI with a button to /team. On 4xx → show error.
 *   3. Logged in + no token: probably a stale link or someone hit the
 *      URL manually. Show a friendly "kein Token" message + Dashboard CTA.
 *
 * No-auth-required is fine here: the JWT itself is the credential. We
 * still require login because we have to associate the invite with a
 * specific User row (we add them as a team member); a logged-out
 * accept doesn't make sense.
 */

const TOKEN_STORAGE_KEY = 'pending_team_invite_token';

export default function TeamInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading | success | already-member | error | no-token
  const [errorMessage, setErrorMessage] = useState('');
  const [teamName, setTeamName] = useState('');

  useEffect(() => {
    // Reconstitute token from sessionStorage if the user just came back
    // from /auth without it in the URL (rare but possible).
    let token = tokenFromUrl;
    if (!token) {
      try {
        token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      } catch (_) { /* private mode */ }
    }

    if (!token) {
      setState('no-token');
      return;
    }

    if (!isAuthenticated()) {
      // Stash + bounce. Returning here after login picks the token
      // back up via sessionStorage above.
      try {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      } catch (_) { /* best effort */ }
      navigate(`/auth?redirect=${encodeURIComponent('/team/invite')}`);
      return;
    }

    // Logged in + have a token → accept it.
    (async () => {
      try {
        const res = await teamAPI.acceptInvite(token);
        setTeamName(res.data?.team_name || '');
        setState('success');
        // Clean up the bouncer-cache key.
        try { sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch (_) {}
      } catch (err) {
        try { sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch (_) {}
        const status = err.response?.status;
        const detail = err.response?.data?.detail || '';
        if (status === 409) {
          setState('already-member');
          setErrorMessage(detail);
        } else {
          setState('error');
          setErrorMessage(
            detail
            || t('teamInvite.failed')
            || 'Einladung konnte nicht angenommen werden.'
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl]);

  return (
    <div className="min-h-screen bg-[#f7f3ea] px-4 py-8 flex items-center justify-center">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex p-3 bg-[#e9efe7] rounded-2xl mb-3">
            <Users className="w-7 h-7 text-[#1f4d3a]" />
          </div>
          <h1 className="text-2xl font-bold text-[#23211a]">
            {t('teamInvite.title') || 'Team-Einladung'}
          </h1>
        </div>

        {state === 'loading' && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
          </div>
        )}

        {state === 'success' && (
          <Card className="glass border-[#1e7a4f]/40">
            <CardContent className="p-8 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-[#1e7a4f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#1e7a4f]">
                {t('teamInvite.success', { team: teamName })
                  || `Willkommen im Team ${teamName ? `„${teamName}"` : ''}!`}
              </h2>
              <p className="text-sm text-[#6f6a5c] max-w-md mx-auto">
                {t('teamInvite.successHelp')
                  || 'Du hast jetzt vollen Zugriff auf alle Real-Content-Funktionen über das Team-Abo.'}
              </p>
              <Button
                onClick={() => navigate('/team')}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              >
                {t('teamInvite.toTeam') || 'Zum Team'}
              </Button>
            </CardContent>
          </Card>
        )}

        {state === 'already-member' && (
          <Card className="glass border-[#c7a869]">
            <CardContent className="p-8 text-center space-y-3">
              <Users className="w-12 h-12 text-[#9a6d1f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#9a6d1f]">
                {t('teamInvite.conflict') || 'Diese Einladung kann nicht angenommen werden'}
              </h2>
              <p className="text-sm text-[#6f6a5c] max-w-md mx-auto">
                {errorMessage || t('teamInvite.conflictHelp')
                  || 'Du gehörst bereits zu einem Team. Verlasse es zuerst, oder kontaktiere den Inhaber.'}
              </p>
              <Button
                onClick={() => navigate('/team')}
                variant="outline"
                className="border-[#d8d0bd] text-[#4a463b]"
              >
                {t('teamInvite.toTeam') || 'Zum Team'}
              </Button>
            </CardContent>
          </Card>
        )}

        {state === 'error' && (
          <Card className="glass border-[#b3543f]/40">
            <CardContent className="p-8 text-center space-y-3">
              <XCircle className="w-12 h-12 text-[#b3543f] mx-auto" />
              <p className="text-sm text-[#a34430]">{errorMessage}</p>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-[#d8d0bd] text-[#4a463b]"
              >
                {t('common.toDashboard') || 'Zum Dashboard'}
              </Button>
            </CardContent>
          </Card>
        )}

        {state === 'no-token' && (
          <Card className="glass border-[#d8d0bd]">
            <CardContent className="p-8 text-center space-y-3">
              <LogIn className="w-10 h-10 text-[#6f6a5c] mx-auto" />
              <p className="text-sm text-[#4a463b]">
                {t('teamInvite.noToken')
                  || 'Kein gültiges Einladungs-Token in der URL. Wenn du den Link aus einer E-Mail geöffnet hast, ist er möglicherweise abgelaufen.'}
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-[#d8d0bd] text-[#4a463b]"
              >
                {t('common.toDashboard') || 'Zum Dashboard'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
